import {
  BrandclubFooter,
  removeTrailingSlash,
  StoreBrandingType,
  UNIVERSAL_STORE_SITE,
} from "@brandclub/common-ui";
import { Outlet } from "react-router-dom";
import { useAppSelector } from "../../../redux/hooks";
import axios from "../../../utils/axios";

import { useSiteContext } from "../../SiteProvider";
import { withFadeTransition } from "../../StoreComponents/StoreTransition";
import { buildFooterConfig } from "./BrandStore/config";
import { useEffect, useMemo, useState } from "react";
import { sumCampaignRewardCounts } from "../../../utils/misc";
import { getRewardAmountsByStatus } from "../../../api";
import AppLink from "../../AppNavigation/AppLink";
import { styled } from "@mui/material";
import { useSignedIn } from "@/utils/hooks/useSignedIn";
import { useApolloClient } from "@apollo/client";
import useStoreType from "@/utils/hooks/useStoreType";

const MainContentContainer = styled("div")({
  height: "100%",
});

const DtcFooterOutlet = () => {
  const apolloClient = useApolloClient();
  const { signedIn } = useSignedIn();
  const appConfig = useAppSelector(({ appConfig }) => appConfig);
  const { siteBrandingType, site, persistentQueryParams, routePrefix } =
    useSiteContext();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const storeType = useStoreType();
  const isDtc = storeType === StoreBrandingType.CustomDTCStore;
  const brandAppConfig = useAppSelector(
    (state) => state.appConfig?.brandAppConfig
  );
  const enableRewards = isDtc
    ? brandAppConfig?.modulesConfig?.rewardsModule?.enabled
    : true;
  const enableMembership = isDtc
    ? brandAppConfig?.modulesConfig?.membershipModule?.enabled
    : true;

  const [rewardSummaryForBrand, setRewardSummaryForBrand] = useState<{
    lifetimeEarnings: number;
    lifetimePayouts: number;
    lifetimeAdjustments: number;
    lockedBalance: number;
    pendingBalance: number;
    unlockedBalance: number;
    cancelledBalance: number;
    hasLoaded: boolean;
  }>({
    lifetimeEarnings: 0,
    lifetimePayouts: 0,
    lifetimeAdjustments: 0,
    lockedBalance: 0,
    pendingBalance: 0,
    unlockedBalance: 0,
    cancelledBalance: 0,
    hasLoaded: false,
  });

  const brandEntity = useAppSelector(({ brandEntity }) => brandEntity);
  const totalRewardCounts = useMemo(() => {
    return brandEntity?.brandclub
      ? sumCampaignRewardCounts(brandEntity.brandclub)
      : 0;
  }, [brandEntity?.brandclub]);

  useEffect(() => {
    (async () => {
      if (brandEntity?.brandclub && userProfile?.userId) {
        const variables = {
          brandIds: brandEntity?.brandclub?.brandIds,
          forceRefresh: true,
        };
        const { data } = await apolloClient.query({
          query: getRewardAmountsByStatus,
          variables,
        });
        if (data && data.RewardSummary) {
          setRewardSummaryForBrand({ ...data.RewardSummary, hasLoaded: true });
        }
      }
    })();
  }, [brandEntity?.brandclub, userProfile, apolloClient]);

  const appLink = appConfig?.domainConfig?.mobileAppLink;

  const footerConfigs = useMemo(() => {
    const config = buildFooterConfig({
      siteBrandingType,
      userRewardAmount:
        rewardSummaryForBrand.lifetimeEarnings -
        rewardSummaryForBrand.lifetimePayouts -
        rewardSummaryForBrand.lifetimeAdjustments,
      userRewardCounts: totalRewardCounts,
      site,
      routePrefix,
      persistentQueryParams,
      signedIn,
      appLink,
      customText: appConfig?.domainConfig?.storeDescription,
      enableRewards,
      enableMembership,
    });

    return [config];
  }, [
    signedIn,
    appConfig?.domainConfig?.storeDescription,
    siteBrandingType,
    rewardSummaryForBrand,
    totalRewardCounts,
    site,
    routePrefix,
    persistentQueryParams,
    appLink,
    enableRewards,
  ]);

  return (
    <div
      id={"dtc_footer"}
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
      }}
    >
      <MainContentContainer className="main-content-container">
        <Outlet />
      </MainContentContainer>

      <BrandclubFooter
        LinkComponent={AppLink}
        axios={axios}
        brandEntity={brandEntity as any}
        footerConfigs={footerConfigs}
        userProfile={userProfile}
        socialLinks={appConfig?.socialLinks}
      />
    </div>
  );
};

export default withFadeTransition(DtcFooterOutlet);
